import { Injectable, NotFoundException } from '@nestjs/common';
import { EntityManager, LoadStrategy, wrap } from '@mikro-orm/core';
import { Newsfeed } from '../entities/newsfeed.entity';
import { CreateNewsfeedDto } from '../dto/create-newsfeed.dto';
import { UpdateNewsfeedDto } from '../dto/update-newsfeed.dto';
import { User } from '../../user/entities/user.entity';
import { FastifyRequest } from 'fastify';

@Injectable()
export class NewsfeedService {
  constructor(private readonly em: EntityManager) {}

  async create(createNewsfeedDto: CreateNewsfeedDto, user: User): Promise<any> {
    const newsfeed = new Newsfeed();
    this.em.assign(newsfeed, {
      title: createNewsfeedDto.title,
      subject: createNewsfeedDto.subject,
      description: createNewsfeedDto.description,
      is_live_edit: createNewsfeedDto.is_live_edit,
      is_public: createNewsfeedDto.is_public,
      user: user,
      audio: createNewsfeedDto.audio,
      tags: createNewsfeedDto.tags.map((tag) => {
        return {
          title: tag,
          newsfeed: newsfeed
        }
      }),
    });
    
    await this.em.persistAndFlush(newsfeed);
    return wrap(newsfeed).toJSON();
  }

  async findAll(page: number = 1, limit: number = 10, params: any = {}, request: FastifyRequest & { user: User }): Promise<{ data: any[], page: number | null, limit: number | null, total: number | null }> {
    let condition = {};
    if(params.my_stories){
      condition['user'] = request.user.id;
    } else { // for home listing
      condition['$or'] = [
        {is_public: true},
        { $and : [{is_public: false}, {user: request.user.id}]}
      ]
    }

    if(params.search){
      condition['$and'] = [
        { $or: [
          { title: { $like: `%${params.search}%` } }, 
          { subject: { $like: `%${params.search}%` } },
          { tags: { title: { $like: `%${params.search}%` } } } // search record by tags.title
        ]}
      ];
    }

    if(params.is_public){
      condition['is_public'] = params.is_public || true;
    }
    const newsfeeds = await this.em.find(
      Newsfeed,
      condition,
      { populate: ['user', 'tags', 'chapter'], limit, offset: (page - 1) * limit, orderBy: { created_at: 'DESC' }, strategy: LoadStrategy.SELECT_IN }
    );
  
    return {
      data: newsfeeds.map(n => wrap(n).toJSON()),
      page,
      limit,
      total: await this.em.count(Newsfeed, condition)
    };
  }

  async findOne(id: number): Promise<any> {
    const newsfeed = await this.em.findOne(Newsfeed, { id }, { populate: ['user', 'tags', 'chapter'] });
    if (!newsfeed) {
      throw new NotFoundException(`Newsfeed with ID "${id}" not found`);
    }
    return wrap(newsfeed).toJSON();
  }

  async update(id: number, updateNewsfeedDto: UpdateNewsfeedDto): Promise<any> {
    const newsfeed = await this.em.findOne(Newsfeed, { id }, { populate: ['user', 'tags', 'chapter'] });
    if (!newsfeed) {
      throw new NotFoundException(`Newsfeed with ID "${id}" not found`);
    }
    Object.assign(newsfeed, updateNewsfeedDto);
    await this.em.persistAndFlush(newsfeed);
    return wrap(newsfeed).toJSON();
  }

  async remove(id: number): Promise<void> {
    const newsfeed = await this.em.findOne(Newsfeed, { id }, { populate: ['user', 'tags'] });
    if (!newsfeed) {
      throw new NotFoundException(`Newsfeed with ID "${id}" not found`);
    }
    await this.em.removeAndFlush(newsfeed);
  }
}
